import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import { find, forEach, get, hasIn, includes, isEmpty, isEqual, isObject, map } from 'lodash';
import { Form, Row, Col, Alert } from 'react-bootstrap';

import * as atlasOps from '../../../api/Atlas/atlas-operations';

import getAtlasPropFromForm from '../../../modules/get-atlas-prop-from-form';
import getAtlasPropsToPass from '../../../modules/get-atlas-props-to-pass';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';
import { urlRegex } from '../../../modules/regex-utils';

import {
  ATLAS_TEMPLATES,
  ATLAS_ELEMENTS,
  ATLAS_PROP_TYPES,
  ANNOTATIONS_TYPES
} from '../../atoms/AtlasAtoms/atlas-elements';
import AtlasContentStep from '../../atoms/AtlasContentStep';
import AtlasStructureStep from '../../atoms/AtlasStructureStep';

import AtlasSelector from '../AtlasSelector';
import EditorButtonRow from '../EditorButtonRow';

import Validation from '../../containers/Validation';

import Styles from './styles';

const { defaultMapSource, defaultGraphicSource, defaultTableSource } = Meteor.settings.public;

const EXTERNAL_LINK_REGEX = new RegExp('ExternalLink');

class AtlasEditor extends Component {
  state = {
    atlasType: atlasOps.getType(this.props.atlas),
    editionStep: atlasOps.getId(this.props.atlas) ? 2 : 0,
    previewProps: getAtlasPropsToPass(this.props.atlas),
    selectedStructure: atlasOps.getAtlasStructure(this.props.atlas),
    showButtons: atlasOps.areButtonsShown(this.props.atlas),
    sharedConfiguration: atlasOps.getSharedConfiguration(this.props.atlas)
  };

  initialState = { ...this.state, editionStep: 0 };

  changeAtlasType = newAtlasType => {
    const { structures } = find(ATLAS_TEMPLATES.properties, ['value', newAtlasType]);
    if (structures.length === 1) {
      this.setState({ atlasType: newAtlasType, editionStep: 2, selectedStructure: structures[0] });
    } else {
      this.setState({ atlasType: newAtlasType, editionStep: 1 });
    }
  };

  changeEditionStep = stepNumber => this.setState({ editionStep: stepNumber });

  changeShowButtons = event => this.setState({ showButtons: event.target.checked });

  changeUserConfiguration = settings => this.setState({ sharedConfiguration: settings });

  handlePreviewProps = form => {
    const { selectedStructure } = this.state;
    const newPreviewProps = map(selectedStructure, element => {
      const elementProperties = find(ATLAS_ELEMENTS.properties, ['value', element]);
      const { props, value } = elementProperties;
      const propsObject = {};
      forEach(props, ({ name, type }, index) => {
        const formObject = getAtlasPropFromForm({ name, value, type, form, index });
        if (type !== ATLAS_PROP_TYPES.STRING || !isEmpty(formObject.propValue))
          propsObject[formObject.propName] = formObject.propValue;
        if (hasIn(formObject, 'propValue.elementProps')) {
          forEach(
            formObject.propValue.elementProps,
            ({ propName: childPropName, propValue: childPropValue }) => {
              if (urlRegex.test(childPropValue)) {
                if (EXTERNAL_LINK_REGEX.test(childPropName))
                  propsObject[`${formObject.propName}ExternalURL`] = childPropValue;
                else propsObject[`${formObject.propName}URL`] = childPropValue;
              }
              if (!hasIn(propsObject, `${formObject.propName}URL`)) {
                switch (formObject.propValue.elementName) {
                  case ATLAS_ELEMENTS.MAPIFRAME:
                    propsObject[`${formObject.propName}URL`] = defaultMapSource;
                    break;
                  case ATLAS_ELEMENTS.GRAPHICIFRAME:
                    propsObject[`${formObject.propName}URL`] = defaultGraphicSource;
                    break;
                  case ATLAS_ELEMENTS.TABLEIFRAME:
                    propsObject[`${formObject.propName}URL`] = defaultTableSource;
                    break;
                  default:
                    propsObject[`${formObject.propName}URL`] = '';
                    break;
                }
              }
            }
          );
        }
      });
      return propsObject;
    });
    this.setState({ previewProps: newPreviewProps });
  };

  handleSelectedStructure = structure => {
    const { selectedStructure } = this.state;
    if (isEqual(selectedStructure, structure)) {
      this.setState({ selectedStructure: [] });
    } else {
      this.setState({ selectedStructure: structure });
    }
  };

  handleSubmit = async form => {
    const { atlas } = this.props;
    const { atlasType, selectedStructure, showButtons, sharedConfiguration } = this.state;

    const existingAtlas = atlasOps.getId(atlas);
    const methodToCall = existingAtlas ? 'atlas.update' : 'atlas.insert';

    const atlasProps = map(selectedStructure, element => {
      const elementProperties = find(ATLAS_ELEMENTS.properties, ['value', element]);
      const { props, value } = elementProperties;
      return map(props, ({ name, type }, index) =>
        getAtlasPropFromForm({
          name,
          value,
          type,
          form,
          index,
          getComponent: false
        })
      );
    });

    const newAtlas = {
      name: form.name.value.trim(),
      atlasType,
      atlasStructure: [],
      gmsConnection: form.gmsConnection.value.trim(),
      showButtons,
      sharedConfiguration
    };

    forEach(selectedStructure, (atlasElement, index) => {
      newAtlas.atlasStructure.push({
        atlasElement,
        atlasProps: atlasProps[index]
      });
    });

    if (atlasType === 'templateScorecardCreateDataset')
      newAtlas.gmsConnection2 = form.gmsConnection2.value.trim();
    if (existingAtlas) newAtlas._id = existingAtlas;

    try {
      const atlasId = await methodPromise(methodToCall, newAtlas);
      this.redirectToAtlasView(form, existingAtlas, atlasId);
    } catch (error) {
      console.error(error);
      handleErrorInClient(error);
    }
  };

  resetAtlasState = () => this.setState(this.initialState);

  redirectToAtlasView(form, existingAtlas, atlasId) {
    const { history } = this.props;
    const confirmation = existingAtlas ? 'Atlas updated!' : 'Atlas added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/atlas/${atlasId}`);
  }

  render() {
    const {
      atlasType,
      editionStep,
      previewProps,
      selectedStructure,
      sharedConfiguration,
      showButtons
    } = this.state;
    const { isBasic } = this.props;
    if (editionStep === 0)
      return <AtlasSelector handleAtlasType={key => this.changeAtlasType(key)} isBasic={isBasic} />;

    const { atlas } = this.props;

    const validationProps = {
      rules: {
        name: {
          required: true
        },
        description: {
          required: false
        },
        imageUrl: {
          required: false
        },
        gmsConnection: {
          required: false
        },
        gmsConnection2: {
          required: false
        },
        showButtons: {
          required: false
        },
        sharedConfiguration: {
          required: false
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss.'
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };

    const { image: atlasImage, structures } = find(ATLAS_TEMPLATES.properties, [
      'value',
      atlasType
    ]);
    const heightToRemove = get(
      find(previewProps, element => hasIn(element, 'navbarHeight')),
      'navbarHeight'
    );

    return (
      <Validation {...validationProps}>
        <Form onSubmit={event => event.preventDefault()}>
          <Row>
            <Col xs={12} md={8}>
              <EditorButtonRow
                resetState={this.resetAtlasState}
                editionStep={editionStep}
                changeEditionStep={this.changeEditionStep}
                item="atlas"
                atlasImage={atlasImage}
                structureEnabled={structures.length !== 1}
              />
              {editionStep === 1 && (
                <AtlasStructureStep
                  changeEditionStep={this.changeEditionStep}
                  handleSelectedStructure={this.handleSelectedStructure}
                  structures={structures}
                  selectedStructure={selectedStructure}
                  atlasImage={atlasImage}
                  atlasType={atlasType}
                />
              )}
              {editionStep === 2 && (
                <AtlasContentStep
                  atlas={atlas}
                  isAnnotationsAtlas={includes(ANNOTATIONS_TYPES, atlasType)}
                  selectedStructure={selectedStructure}
                  showButtons={showButtons}
                  changeShowButtons={this.changeShowButtons}
                  handlePreviewProps={this.handlePreviewProps}
                  changeUserConfiguration={this.changeUserConfiguration}
                  sharedConfiguration={sharedConfiguration}
                />
              )}
            </Col>
            <Col xs={12} md={4}>
              <Alert variant="info">Preview</Alert>
              {!isEmpty(selectedStructure) && (
                <Styles.PreviewContainer>
                  {map(selectedStructure, (element, index) => {
                    const { component } = find(ATLAS_ELEMENTS.properties, ['value', element]);
                    const propsToAdd = previewProps[index];
                    forEach(propsToAdd, (prop, key) => {
                      if (isObject(prop) && hasIn(prop, ['component']))
                        propsToAdd[key] = prop.component;
                    });
                    if (isObject(propsToAdd) && !hasIn(propsToAdd, 'navbarHeight'))
                      propsToAdd.heightToRemove = heightToRemove;
                    return React.cloneElement(component, {
                      isPreview: true,
                      key: `${element}-preview`,
                      showButtons,
                      ...propsToAdd
                    });
                  })}
                </Styles.PreviewContainer>
              )}
            </Col>
          </Row>
        </Form>
      </Validation>
    );
  }
}

AtlasEditor.defaultProps = {
  atlas: {
    name: '',
    atlasType: '',
    atlasProps: {},
    showButtons: true
  },
  isAnnotationsAtlas: false
};

AtlasEditor.propTypes = {
  atlas: PropTypes.object,
  history: PropTypes.object.isRequired,
  isAnnotationsAtlas: PropTypes.bool,
  isBasic: PropTypes.bool.isRequired
};

export default AtlasEditor;
